// Cầu nối frontend ↔ backend /lesson-builder.
// Khi ĐÃ đăng nhập (có JWT) → gọi API thật; khi chưa/đứt mạng → fallback localStorage
// để vẫn dùng/test được offline. Giữ nguyên shape LessonDoc/CourseDoc.
import { API_CONFIG } from '../../../config/api.config'
import { openProTrial } from '../../../utils/proTrial'
import { aiHeaders, aiVideoModel, handleAIError } from '../../../services/ai/aiConfig'
import { lessonStorage } from '../lib/lessonStorage'
import { courseStorage } from '../lib/courseStorage'
import type { LessonDoc } from '../types/LessonDoc'
import type { CourseDoc } from '../types/CourseDoc'

const API_ROOT = API_CONFIG.python.apiUrl
const BASE = `${API_ROOT}/lesson-builder`

function getToken(): string {
  return (
    localStorage.getItem('jwt_token') ||
    localStorage.getItem('auth_token') ||
    localStorage.getItem('token') ||
    ''
  )
}

export function isAuthed(): boolean {
  return !!getToken()
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
      ...(init?.headers || {}),
    },
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    // Thiếu quyền PRO (backend require_pro) → tự bật popup dùng thử NGAY TẠI CHỖ.
    if (res.status === 403 && /pro|dùng thử|nâng cấp/i.test(detail)) openProTrial('ebook')
    handleAIError(res.status)   // 428 → mở hộp thoại Cấu hình AI
    throw new Error(`API ${res.status}: ${detail}`)
  }
  return res.json()
}

/**
 * Như req() nhưng KÈM khoá AI của người dùng. Chỉ dùng cho endpoint thực sự gọi AI —
 * không gắn khoá vào mọi lời gọi để khoá không đi tới nơi không cần.
 */
function reqAI<T>(path: string, init?: RequestInit, kind: 'text' | 'voice' | 'video' = 'text'): Promise<T> {
  return req<T>(path, { ...init, headers: { ...(init?.headers || {}), ...aiHeaders(kind) } })
}

/** Header cho lời gọi AI dạng multipart (upload Word) — FormData tự đặt Content-Type. */
function aiFormHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${getToken()}`, ...aiHeaders('text') }
}

// ---------------- Lessons ----------------
export const lessonApi = {
  async list(): Promise<LessonDoc[]> {
    if (!isAuthed()) return lessonStorage.list()
    return await req<LessonDoc[]>('/lessons')
  },
  async sharedWithMe(): Promise<LessonDoc[]> {
    if (!isAuthed()) return []
    return await req<LessonDoc[]>('/lessons/shared')
  },
  async get(id: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.get(id)
    try {
      return await req<LessonDoc>(`/lessons/${id}`)
    } catch {
      return lessonStorage.get(id)
    }
  },
  async getBySlug(slug: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.getBySlug(slug)
    try {
      return await req<LessonDoc>(`/lessons/by-slug/${encodeURIComponent(slug)}`)
    } catch {
      return lessonStorage.getBySlug(slug)
    }
  },
  async save(doc: LessonDoc): Promise<LessonDoc> {
    if (!isAuthed()) return lessonStorage.save(doc)
    try {
      return await req<LessonDoc>('/lessons', { method: 'POST', body: JSON.stringify(doc) })
    } catch {
      return lessonStorage.save(doc)
    }
  },
  async publish(id: string): Promise<LessonDoc | null> {
    if (!isAuthed()) return lessonStorage.publish(id)
    try {
      return await req<LessonDoc>(`/lessons/${id}/publish`, { method: 'POST' })
    } catch {
      return lessonStorage.publish(id)
    }
  },
  async remove(id: string): Promise<void> {
    if (!isAuthed()) {
      lessonStorage.remove(id)
      return
    }
    // Gọi backend xoá; KHÔNG nuốt lỗi (trước đây catch âm thầm → backend còn nguyên).
    await req(`/lessons/${id}`, { method: 'DELETE' })
    lessonStorage.remove(id)   // dọn luôn bản nháp local nếu có
  },
}

// ---------------- Courses ----------------
export const courseApi = {
  async list(): Promise<CourseDoc[]> {
    if (!isAuthed()) return courseStorage.list()
    return await req<CourseDoc[]>('/courses')
  },
  async sharedWithMe(): Promise<CourseDoc[]> {
    if (!isAuthed()) return []
    return await req<CourseDoc[]>('/courses/shared')
  },
  async get(id: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.get(id)
    try {
      return await req<CourseDoc>(`/courses/${id}`)
    } catch {
      return courseStorage.get(id)
    }
  },
  async getBySlug(slug: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.getBySlug(slug)
    try {
      return await req<CourseDoc>(`/courses/by-slug/${encodeURIComponent(slug)}`)
    } catch {
      return courseStorage.getBySlug(slug)
    }
  },
  async save(doc: CourseDoc): Promise<CourseDoc> {
    if (!isAuthed()) return courseStorage.save(doc)
    // Khi ĐÃ đăng nhập: KHÔNG nuốt lỗi → ném ra để UI báo (tránh "tưởng đã lưu" mà server chưa lưu).
    return await req<CourseDoc>('/courses', { method: 'POST', body: JSON.stringify(doc) })
  },
  async publish(id: string): Promise<CourseDoc | null> {
    if (!isAuthed()) return courseStorage.publish(id)
    return await req<CourseDoc>(`/courses/${id}/publish`, { method: 'POST' })
  },
  async remove(id: string): Promise<void> {
    if (!isAuthed()) {
      courseStorage.remove(id)
      return
    }
    await req(`/courses/${id}`, { method: 'DELETE' })
    courseStorage.remove(id)
  },
  // ── Ghi danh & tiến độ (cần đăng nhập) ──
  // Trả {ok}; nếu khoá PRO mà thiếu quyền → throw Error có message để UI hiển thị.
  async enroll(courseId: string): Promise<void> {
    if (!isAuthed()) throw new Error('Vui lòng đăng nhập để ghi danh.')
    await req(`/courses/${courseId}/enroll`, { method: 'POST' })
  },
  async progress(courseId: string): Promise<CourseProgress | null> {
    if (!isAuthed()) return null
    try {
      return await req<CourseProgress>(`/courses/${courseId}/progress`)
    } catch {
      return null
    }
  },
  async myEnrollments(): Promise<CourseEnrollmentItem[]> {
    if (!isAuthed()) return []
    try {
      return await req<CourseEnrollmentItem[]>('/my/enrollments')
    } catch {
      return []
    }
  },
  // ── Catalog công khai ──
  async publicCourses(): Promise<PublicCourse[]> {
    try {
      return await req<PublicCourse[]>('/courses/public')
    } catch {
      return []
    }
  },
  // ── Thống kê cho giáo viên ──
  async teacherCoursesStats(): Promise<TeacherCourseStat[]> {
    try {
      return await req<TeacherCourseStat[]>('/teacher/courses-stats')
    } catch {
      return []
    }
  },
  async stats(courseId: string): Promise<CourseStats | null> {
    try {
      return await req<CourseStats>(`/courses/${courseId}/stats`)
    } catch {
      return null
    }
  },
  // ── Đánh giá sao ──
  async getRating(courseId: string): Promise<CourseRating> {
    try {
      return await req<CourseRating>(`/courses/${courseId}/rating`)
    } catch {
      return { avg: 0, count: 0, myStars: null }
    }
  },
  async rate(courseId: string, stars: number, comment?: string): Promise<CourseRating> {
    const r = await req<{ ok: boolean; avg: number; count: number; myStars: number }>(
      `/courses/${courseId}/rate`, { method: 'POST', body: JSON.stringify({ stars, comment }) }
    )
    return { avg: r.avg, count: r.count, myStars: r.myStars }
  },
  // ── Chứng chỉ hoàn thành ──
  async certificateInfo(courseId: string): Promise<CertificateInfo> {
    if (!isAuthed()) return { certificate: null, eligible: false, total: 0, completed: 0, percent: 0 }
    try {
      return await req<CertificateInfo>(`/courses/${courseId}/certificate`)
    } catch {
      return { certificate: null, eligible: false, total: 0, completed: 0, percent: 0 }
    }
  },
  async issueCertificate(courseId: string): Promise<Certificate> {
    const r = await req<{ certificate: Certificate }>(`/courses/${courseId}/certificate`, { method: 'POST' })
    return r.certificate
  },
  // ── Giao khoá cho lớp (cohort) ──
  async myClasses(): Promise<ClassInfo[]> {
    try { return await req<ClassInfo[]>('/my-classes') } catch { return [] }
  },
  async assignClass(courseId: string, className: string): Promise<{ assigned: number; skipped_no_account: number }> {
    return req(`/courses/${courseId}/assign-class`, { method: 'POST', body: JSON.stringify({ class_name: className }) })
  },
  // ── Sổ điểm hợp nhất (GV) ──
  async gradebook(): Promise<Gradebook> {
    try {
      return await req<Gradebook>('/gradebook')
    } catch {
      return { columns: [], students: [], total_students: 0, total_activities: 0 }
    }
  },
}

export interface ClassInfo { name: string; total: number; linked: number }
export interface GradebookColumn { key: string; type: 'exam' | 'lesson' | 'assignment'; title: string; max: number }
export interface GradebookCell { score: number; max: number; percent: number }
export interface GradebookStudent { name: string; email: string; scores: Record<string, GradebookCell>; avgPercent: number; count: number }
export interface Gradebook { columns: GradebookColumn[]; students: GradebookStudent[]; total_students: number; total_activities: number }

// Tra cứu chứng chỉ CÔNG KHAI (không cần đăng nhập) — dùng cho trang /chung-chi/:code.
export async function verifyCertificate(code: string): Promise<CertificateVerify> {
  try {
    const res = await fetch(`${BASE}/certificate/verify/${encodeURIComponent(code)}`)
    if (!res.ok) return { valid: false }
    return await res.json()
  } catch {
    return { valid: false }
  }
}

export interface Certificate { code: string; learner_name?: string; course_title?: string; issuer_name?: string; issued_at?: string }
export interface CertificateInfo { certificate: Certificate | null; eligible: boolean; total: number; completed: number; percent: number }
export interface CertificateVerify { valid: boolean; code?: string; learner_name?: string; course_title?: string; issuer_name?: string; issued_at?: string }

export interface CourseRating { avg: number; count: number; myStars: number | null }

export interface CourseProgressLesson { id: string; slug: string; title: string; completed: boolean }
export interface CourseProgress {
  enrolled: boolean
  total: number
  completed: number
  percent: number
  nextSlug: string | null
  lessons: CourseProgressLesson[]
}
export interface CourseEnrollmentItem { course_id: string; slug: string; title: string; coverImage?: string; dueDate?: string; enrolledAt?: string }

export interface PublicCourse {
  id: string; slug: string; title: string; subtitle?: string; coverImage?: string
  category?: string; grade?: string; level?: string; access?: 'free' | 'pro'; instructorName?: string
  ratingAvg?: number; ratingCount?: number
  lessonCount: number; enrolledCount: number
}
export interface TeacherCourseStat { id: string; slug: string; title: string; status: string; lessonCount: number; enrolledCount: number }
export interface CourseStatsLesson { id: string; title: string; slug: string; started: number; completed: number; avgScore: number | null; aiAsked?: number; avgContent?: number | null }
export interface CourseStatsStudent { studentName?: string; studentEmail?: string; className?: string; completed: number; total: number; percent: number; avgScore: number | null }
export interface CourseStatsClass { className: string; students: number; completedCourse: number; avgPercent: number; avgScore: number | null }
export interface CourseStats {
  course: { id: string; title: string; slug: string }
  overview: { enrolled: number; completedCourse: number; avgPercent: number; lessons: number }
  perLesson: CourseStatsLesson[]
  perStudent: CourseStatsStudent[]
  perClass?: CourseStatsClass[]
}

// ---------------- Sharing ----------------
export interface SharePayload {
  resource_type: 'lesson' | 'course'
  resource_id: string
  scope: 'all' | 'teacher'
  target_email?: string
}

export const shareApi = {
  async share(payload: SharePayload): Promise<{ id: string; ok: boolean }> {
    return req('/share', { method: 'POST', body: JSON.stringify(payload) })
  },
}

// ---------------- Bài tập tự luận (GV chấm tay) ----------------
export interface RubricItem { label: string; max: number; cau?: string }
export interface Assignment {
  id: string; slug?: string; owner_id?: string; course_id?: string | null
  title: string; prompt: string; max_score: number; due_date?: string; allow_file?: boolean
  attachment_url?: string; attachment_type?: '' | 'image' | 'pdf'; rubric?: RubricItem[]
  created_at?: string; submissionCount?: number; gradedCount?: number
}
export interface Submission {
  id: string; assignment_id: string; student_id: string; student_name?: string; student_email?: string
  content: string; file_url?: string; files?: string[]; itemScores?: number[]; annotations?: string[]
  status: 'submitted' | 'graded'; score: number | null
  feedback?: string; feedback_audio_url?: string; feedback_video_url?: string; submitted_at?: string; graded_at?: string
}
export interface AssignmentWithMine extends Assignment { mySubmission: Submission | null }

// Upload multipart (KHÔNG set Content-Type để trình duyệt tự thêm boundary).
async function uploadForm<T>(path: string, file: File): Promise<T> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers: { Authorization: `Bearer ${getToken()}` }, body: fd })
  if (!res.ok) { const d = await res.text().catch(() => ''); throw new Error(`API ${res.status}: ${d}`) }
  return res.json()
}

export const assignmentApi = {
  async listMine(): Promise<Assignment[]> {
    try { return await req<Assignment[]>('/assignments') } catch { return [] }
  },
  async save(a: Partial<Assignment>): Promise<Assignment> {
    return req<Assignment>('/assignments', { method: 'POST', body: JSON.stringify(a) })
  },
  async remove(id: string): Promise<void> {
    await req(`/assignments/${id}`, { method: 'DELETE' })
  },
  // GV tải ĐỀ (ảnh/PDF giữ nguyên; Word .docx → print sang PDF giữ nguyên format)
  async uploadAttachment(id: string, file: File): Promise<Assignment> {
    return uploadForm<Assignment>(`/assignments/${id}/attachment`, file)
  },
  async submissions(id: string): Promise<{ assignment: Assignment; submissions: Submission[] }> {
    return req(`/assignments/${id}/submissions`)
  },
  // Chấm theo rubric (điểm từng mục) → backend tự cộng; hoặc 1 điểm tổng (score)
  async grade(sid: string, opts: { score?: number; itemScores?: number[]; feedback?: string; feedbackVideoUrl?: string }): Promise<Submission> {
    return req<Submission>(`/submissions/${sid}/grade`, { method: 'POST', body: JSON.stringify({ score: opts.score, item_scores: opts.itemScores, feedback: opts.feedback || '', feedback_video_url: opts.feedbackVideoUrl || '' }) })
  },
  async courseAssignments(courseId: string): Promise<AssignmentWithMine[]> {
    if (!isAuthed()) return []
    try { return await req<AssignmentWithMine[]>(`/courses/${courseId}/assignments`) } catch { return [] }
  },
  async get(id: string): Promise<{ assignment: Assignment; mySubmission: Submission | null } | null> {
    try { return await req(`/assignments/${id}`) } catch { return null }
  },
  // HS tải file lời giải (ảnh/PDF) → trả url để gắn vào bài nộp
  async uploadFile(id: string, file: File): Promise<{ url: string }> {
    return uploadForm<{ url: string }>(`/assignments/${id}/upload-file`, file)
  },
  // Máy scan: server (OpenCV) tìm 4 góc tờ giấy trên ảnh đã thu nhỏ — toạ độ 0..1, TL,TR,BR,BL
  async detectPaper(file: File): Promise<{ found: boolean; corners: [number, number][] | null }> {
    return uploadForm(`/scan/detect`, file)
  },
  // GV tải ảnh ĐÃ GHI CHÚ/BÚT (PNG) lên bài nộp → HS xem; trả submission đã cập nhật
  async uploadAnnotation(sid: string, file: File): Promise<Submission> {
    return uploadForm<Submission>(`/submissions/${sid}/annotation`, file)
  },
  async deleteAnnotation(sid: string, url: string): Promise<Submission> {
    return req<Submission>(`/submissions/${sid}/annotation`, { method: 'DELETE', body: JSON.stringify({ url }) })
  },
  async uploadFeedbackAudio(sid: string, file: File): Promise<Submission> {
    return uploadForm<Submission>(`/submissions/${sid}/feedback-audio`, file)
  },
  async deleteFeedbackAudio(sid: string): Promise<Submission> {
    return req<Submission>(`/submissions/${sid}/feedback-audio`, { method: 'DELETE' })
  },
  async submit(id: string, content: string, files?: string[]): Promise<Submission> {
    return req<Submission>(`/assignments/${id}/submit`, { method: 'POST', body: JSON.stringify({ content, files: files || [] }) })
  },
  // GV: bài chờ chấm (gom mọi bài tập)
  async toGrade(): Promise<{ count: number; items: ToGradeItem[] }> {
    try { return await req('/to-grade') } catch { return { count: 0, items: [] } }
  },
  // HS: việc cần làm (bài tập trong khoá đã ghi danh)
  async myTodo(): Promise<TodoItem[]> {
    if (!isAuthed()) return []
    try { return await req<TodoItem[]>('/my-todo') } catch { return [] }
  },
}

export interface ToGradeItem { submission_id: string; assignment_id: string; assignment_title: string; student_name?: string; student_email?: string; submitted_at?: string }
export interface TodoItem { assignment_id: string; title: string; course_id?: string | null; due_date?: string; max_score: number; status: 'none' | 'submitted' | 'graded'; score: number | null }

// ---------------- Xuất chuẩn: SCORM (zip) + xAPI (JSON) ----------------
async function downloadBlob(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } })
  if (!res.ok) throw new Error(`API ${res.status}`)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const _safe = (s?: string) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'export'

export interface ScormPreflight {
  ok: boolean
  errors: string[]
  warnings: string[]
  stats: {
    lessons: number
    cards: number
    scorableQuestions: number
    images: number
    externalMedia: number
    onlineOnlyBlocks: number
  }
  items?: Array<{ lessonId: string; title?: string; ok: boolean; errors: string[]; warnings: string[]; stats: Record<string, number> }>
}

export const exportApi = {
  preflightLesson: (id: string) => req<ScormPreflight>(`/lessons/${id}/scorm/preflight`),
  preflightCourse: (id: string) => req<ScormPreflight>(`/courses/${id}/scorm/preflight`),
  scormLesson: (id: string, name?: string) => downloadBlob(`/lessons/${id}/scorm`, `scorm-${_safe(name)}.zip`),
  scormCourse: (id: string, name?: string) => downloadBlob(`/courses/${id}/scorm`, `scorm-${_safe(name)}.zip`),
  async xapi(): Promise<number> {
    const res = await fetch(`${API_ROOT}/learning/xapi/export`, { headers: { Authorization: `Bearer ${getToken()}` } })
    if (!res.ok) throw new Error(`API ${res.status}`)
    const data = await res.json() as { count: number }
    if (!data.count) return 0   // chưa có bản ghi học tập → KHÔNG tải file rỗng (caller báo)
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'xapi-statements.json'; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 1000)
    return data.count
  },
}

export interface RecorderSession {
  token: string
  expiresIn: number
  uploadUrl: string
  protocol: string
}

export const recorderApi = {
  windowsDownloadUrl(): string {
    return `${API_ROOT}/lesson-builder/recorder/download/windows`
  },
  async createSession(lessonId: string, cardId: string): Promise<RecorderSession> {
    const session = await req<RecorderSession>('/recorder/session', {
      method: 'POST',
      body: JSON.stringify({ lesson_id: lessonId, card_id: cardId }),
    })
    const uploadUrl = new URL(`${API_ROOT}/lesson-builder/recorder/upload`, window.location.origin).toString()
    return { ...session, uploadUrl }
  },
}

// ---------------- Video bài giảng ----------------
// aiSettingsApi (lưu khoá Gemini trong DB) ĐÃ BỎ: khoá nay do người dùng nhập trên
// trình duyệt và gửi kèm header — xem services/ai/aiConfig.

export interface VideoSceneRaw {
  voiceover: string; caption: string
  visualType: 'sim' | 'formula' | 'image' | 'text' | 'video'
  simId: string | null; formula: string; durationSec: number
  imageSrc?: string | null   // url ảnh kho (AI chọn từ image-catalog)
  videoUrl?: string | null   // url video kho (AI chọn từ video-catalog)
  setting?: string; action?: string; presenter?: boolean
  presenterAction?: string; prompt?: string
}
export interface BankVideo { youtubeId: string; url: string; title: string }
export const videoApi = {
  async generateScript(input: {
    title: string; source_text?: string;
    lop?: number; chuong?: number; bai?: number;
    availableSims?: { id: string; type: string; title: string }[]
  }): Promise<{ scenes: VideoSceneRaw[]; model: string; usedOwnKey: boolean }> {
    return reqAI('/video/script', { method: 'POST', body: JSON.stringify(input) }, 'video')
  },
  // Kho video khoa học theo bài (để GV chèn "video đã có").
  async catalog(lop?: number, chuong?: number, bai?: number): Promise<BankVideo[]> {
    if (!lop || !chuong || !bai) return []
    try {
      const r = await req<{ videos: BankVideo[] }>(`/video-catalog?lop=${lop}&chuong=${chuong}&bai=${bai}`)
      return r.videos || []
    } catch { return [] }
  },
}

// Veo — sinh clip video AI (chỉ cho cảnh minh hoạ; dùng khoá Gemini của GV, họ tự trả phí).
export const veoApi = {
  async start(prompt: string, model?: string, aspectRatio?: string): Promise<{ operation: string; model: string }> {
    return reqAI('/video/veo/start', {
      method: 'POST',
      body: JSON.stringify({ prompt, model: model || aiVideoModel() || undefined, aspectRatio }),
    }, 'video')
  },
  async result(operation: string, lessonId: string): Promise<{ done: boolean; url?: string }> {
    return reqAI('/video/veo/result', { method: 'POST', body: JSON.stringify({ operation, lesson_id: lessonId }) }, 'video')
  },
}

// ---------------- Kênh giao tiếp chính thức: bảng tin + hỏi đáp ----------------
export interface Announcement {
  id: string; course_id: string; owner_id: string; class_name?: string
  title: string; body: string; is_pinned: boolean; created_at?: string; updated_at?: string
}

export interface LessonQuestion {
  id: string; lesson_id: string; course_id?: string | null
  author_id: string; author_name: string; author_email?: string
  body: string; answer: string; answered_by?: string | null; answered_by_name?: string
  answered_at?: string | null; is_resolved: boolean; created_at?: string; updated_at?: string
}

export const channelApi = {
  async announcements(courseId: string): Promise<Announcement[]> {
    if (!isAuthed() || !courseId) return []
    try { return await req<Announcement[]>(`/courses/${courseId}/announcements`) } catch { return [] }
  },
  async createAnnouncement(courseId: string, data: { title: string; body?: string; class_name?: string; is_pinned?: boolean }): Promise<Announcement> {
    return req<Announcement>(`/courses/${courseId}/announcements`, { method: 'POST', body: JSON.stringify(data) })
  },
  async removeAnnouncement(id: string): Promise<void> {
    await req(`/announcements/${id}`, { method: 'DELETE' })
  },
  async questions(lessonId: string, courseId?: string): Promise<LessonQuestion[]> {
    if (!isAuthed() || !lessonId) return []
    const qs = courseId ? `?course_id=${encodeURIComponent(courseId)}` : ''
    try { return await req<LessonQuestion[]>(`/lessons/${lessonId}/questions${qs}`) } catch { return [] }
  },
  async askQuestion(lessonId: string, body: string, courseId?: string): Promise<LessonQuestion> {
    return req<LessonQuestion>(`/lessons/${lessonId}/questions`, { method: 'POST', body: JSON.stringify({ body, course_id: courseId }) })
  },
  async answerQuestion(questionId: string, answer: string, isResolved = true): Promise<LessonQuestion> {
    return req<LessonQuestion>(`/lesson-questions/${questionId}/answer`, { method: 'POST', body: JSON.stringify({ answer, is_resolved: isResolved }) })
  },
  async hideQuestion(questionId: string): Promise<void> {
    await req(`/lesson-questions/${questionId}`, { method: 'DELETE' })
  },
}
// ---------------- Thông báo trong ứng dụng ----------------
export interface AppNotification { id: string; kind: string; title: string; body: string; link: string; is_read: boolean; created_at?: string }
export interface NotificationList { unread: number; items: AppNotification[] }

export const notificationApi = {
  async list(): Promise<NotificationList> {
    if (!isAuthed()) return { unread: 0, items: [] }
    try { return await req<NotificationList>('/notifications') } catch { return { unread: 0, items: [] } }
  },
  async markRead(id?: string): Promise<void> {
    if (!isAuthed()) return
    try { await req('/notifications/read', { method: 'POST', body: JSON.stringify(id ? { id } : {}) }) } catch { /* noop */ }
  },
}

// ---------------- Kết quả học tập ----------------
export interface ResultPayload {
  card_id?: string
  lesson_slug?: string
  score: number
  max_score: number
  total: number
  correct: number
  detail?: any
}

export const resultApi = {
  // Nộp kết quả (cần đăng nhập). Không đăng nhập → bỏ qua, vẫn chấm phía client.
  async submit(lessonId: string, payload: ResultPayload): Promise<void> {
    if (!isAuthed() || !lessonId) return
    try {
      await req(`/lessons/${lessonId}/submit`, { method: 'POST', body: JSON.stringify(payload) })
    } catch {
      /* không chặn trải nghiệm học nếu lưu lỗi */
    }
  },
  // Danh sách kết quả (chủ bài / admin)
  async list(lessonId: string): Promise<any[]> {
    try {
      return await req(`/lessons/${lessonId}/results`)
    } catch {
      return []
    }
  },
  // Tình trạng làm trắc nghiệm PMaker của HS hiện tại trong bài → {slug: {done, percent}}
  async quizStatus(lessonId: string): Promise<Record<string, { done: boolean; percent: number | null }>> {
    if (!isAuthed() || !lessonId) return {}
    try {
      return await req(`/lessons/${lessonId}/quiz-status`)
    } catch {
      return {}
    }
  },
  // Câu trả lời PHIẾU HỌC TẬP của HS (chủ bài / admin xem)
  async worksheetAll(lessonId: string): Promise<{ students: { student_id: string; name: string; email: string; answers: { item_key: string; answer: string; self_assess: string }[] }[] }> {
    try {
      return await req(`/lessons/${lessonId}/worksheet-answers/all`)
    } catch {
      return { students: [] }
    }
  },
}

// ---------------- Word → Bài học AI (Smart Course Converter) ----------------
// Gửi .docx lên server: parser pmaker (MathType→LaTeX, tách ảnh) + AI bám sát tài liệu
// → trả ContentItem[] (gồm block quiz tương tác) để tạo thẻ "rich" render bằng ContentRenderer.
export async function wordToLessonItems(
  file: File
): Promise<{ items: any[]; title: string; image_count: number }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE}/word-to-lesson`, {
    method: 'POST',
    headers: aiFormHeaders(),
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    handleAIError(res.status)
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}

// ---------------- Word → AI 2 bước: KHUNG → SINH CHI TIẾT ----------------
export interface OutlineTopic { heading: string; hint?: string }
export interface OutlineSection { title: string; kind: 'mo_dau' | 'chinh' | 'ket_thuc'; topics: OutlineTopic[] }
export interface LessonOutline { title: string; sections: OutlineSection[] }

// B1: tải .docx → AI đề xuất khung. Trả source_id để dùng cho bước generate.
export async function aiOutline(
  file: File
): Promise<{ success: boolean; source_id: string; title: string; image_count: number; outline: LessonOutline }> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${BASE}/ai/outline`, {
    method: 'POST',
    headers: aiFormHeaders(),
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    handleAIError(res.status)
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}

// B2: gửi khung (đã GV chỉnh) → AI sinh chi tiết từng phần (items: ContentItem[]).
export async function aiGenerate(
  sourceId: string,
  outline: LessonOutline,
  ctx?: { lop?: number; chuong?: number; bai?: number }
): Promise<{ success: boolean; title: string; sections: { title: string; kind: string; items: any[] }[]; image_count: number }> {
  return reqAI('/ai/generate', { method: 'POST', body: JSON.stringify({ source_id: sourceId, outline, ctx }) })
}

/**
 * Lồng tiếng 1 phần bài giảng (hiện dùng cho phần Khởi động): server "dịch văn nói + đọc công thức
 * thành lời Việt" rồi TTS → trả URL mp3 (đã lưu cùng bài). Cần đăng nhập GV PRO; có trừ điểm AI.
 */
export async function narrateSection(
  lessonId: string,
  sectionId: string,
  text: string,
  voice?: string,
  kind?: string   // loại phần: 'warmup' → chỉ ĐẶT VẤN ĐỀ/gợi mở (không giải thích); khác → diễn đạt rõ
): Promise<{ success: boolean; url: string; script: string }> {
  return reqAI('/voice/narrate', {
    method: 'POST',
    body: JSON.stringify({ lesson_id: lessonId, section_id: sectionId, text, voice, kind }),
  }, 'voice')
}

/**
 * Tải lên / ghi âm audio cho 1 phần bài giảng — KHÔNG dùng AI, KHÔNG trừ điểm.
 * Lưu cùng thư mục audio/ như narrate, trả URL để gắn vào audioUrl của phần đó.
 */
export async function uploadSectionAudio(
  lessonId: string,
  sectionId: string,
  blob: Blob,
  filename = 'audio'
): Promise<{ success: boolean; url: string }> {
  const fd = new FormData()
  fd.append('file', blob, filename)
  fd.append('lesson_id', lessonId)
  fd.append('section_id', sectionId)
  const res = await fetch(`${BASE}/voice/upload`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: fd,
  })
  if (!res.ok) {
    const t = await res.text().catch(() => '')
    throw new Error(`API ${res.status}: ${t}`)
  }
  return res.json()
}

/** Kho video YouTube theo bài (GV đặt file link tại bank/lesson-videos/L#/C#/B#). */
export async function fetchVideoCatalog(
  lop?: number, chuong?: number, bai?: number
): Promise<{ youtubeId: string; url: string; title: string }[]> {
  if (!lop || !chuong || !bai) return []
  try {
    const r = await req<{ videos: any[] }>(`/video-catalog?lop=${lop}&chuong=${chuong}&bai=${bai}`)
    return r?.videos || []
  } catch {
    return []
  }
}
